import InvalidArgumentError from "../../Errors/InvalidArgumentError";
/**
 * NHS Arrhythmia Info: https://www.nhs.uk/conditions/arrhythmia/
 * BHF: https://www.bhf.org.uk/informationsupport/how-a-healthy-heart-works/your-heart-rate
 * HEE Tachycardia Factsheet: https://www.hee.nhs.uk/sites/default/files/documents/Tachycardia.pdf
 * */
export default class NationalHealthServiceHR {
    /**
     * Constructor
     */
    constructor(heartRate) {
        this._validateHeartRate(heartRate);
        this._heartRate = heartRate;
    }

    /**
     * Returns the given Heart Rate value
     * @returns {Number}
     */
    get() {
        return this._heartRate;
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get RiskName() {
        return "HEART_RATE";
    }

    // FIXME: Should this be the concern of the Guideline? Might be better suited to the Risk model
    static get GuidelineName() {
        return "NHS_HR";
    }

    /**
     * Validates the given Heart Rate value. It is intended to be a positive number
     *
     * @returns {boolean}
     * @private
     */
    _validateHeartRate(heartRate) {
        if (!(typeof heartRate === "number") || Number.isNaN(heartRate) || heartRate < 0) {
            throw new InvalidArgumentError("Heart Rate must be a valid number greater than or equal to 0");
        }

        return true;
    }

    /**
     * Returns if the given Heart Rate is in the 'low' range
     *
     * @returns {boolean}
     */
    isLow() {
        return this._heartRate < 60;
    }

    /**
     * Returns if the given Heart Rate is in the 'normal' range
     *
     * @returns {boolean}
     */
    isNormal() {
        return this._heartRate >= 60 && this._heartRate <= 100;
    }

    /**
     * Returns if the given Heart Rate is in the 'high' range
     *
     * @returns {boolean}
     */
    isHigh() {
        return this._heartRate > 100;
    }

    /**
     * Returns an object representing all labels corresponding to this guideline defination
     *
     * @returns {object}
     */
    static get labels() {
        return {
            low: "Low",
            normal: "Normal",
            high: "High"
        };
    }

    /**
     * Returns an object representing various data for each Heart Rate range
     *
     * @returns {object}
     */
    static get ranges() {
        return {
            low: {
                key: "low",
                label: "Low",
                range: "< 60"
            },

            normal: {
                key: "normal",
                label: "Normal",
                range: "60 - 100"
            },

            high: {
                key: "high",
                label: "High",
                range: "100+"
            }
        };
    }

    /**
     * returns help information about the calculated range
     *
     * @returns {string}
     */
    get rangeInfo() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = NationalHealthServiceHR.ranges.low.range;
                break;
            case this.isNormal():
                info = NationalHealthServiceHR.ranges.normal.range;
                break;
            case this.isHigh():
                info = NationalHealthServiceHR.ranges.high.range;
                break;
        }

        return info;
    }

    /**
     * returns key about the calculated range
     *
     * @returns {string}
     */
    get key() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = NationalHealthServiceHR.ranges.low.key;
                break;
            case this.isNormal():
                info = NationalHealthServiceHR.ranges.normal.key;
                break;
            case this.isHigh():
                info = NationalHealthServiceHR.ranges.high.key;
                break;
        }

        return info;
    }

    /**
     * returns label for the calculated range
     *
     * @returns {string}
     */
    get label() {
        let info = "";
        switch (true) {
            case this.isLow():
                info = NationalHealthServiceHR.ranges.low.label;
                break;
            case this.isNormal():
                info = NationalHealthServiceHR.ranges.normal.label;
                break;
            case this.isHigh():
                info = NationalHealthServiceHR.ranges.high.label;
                break;
        }

        return info;
    }

    /**
     * Calculates a risk level number between 0 - 1 for each rating
     *
     * @return {number}
     */
    get riskLevel() {
        let totalRiskLevel = 3,
            riskLevel = 0;
        switch (true) {
            case this.isLow():
                riskLevel = 1 / (totalRiskLevel + 1);
                break;
            case this.isNormal():
                riskLevel = 2 / (totalRiskLevel + 1);
                break;
            case this.isHigh():
                riskLevel = 3 / (totalRiskLevel + 1);
                break;
        }

        return riskLevel;
    }
}
